import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Box, CardContent, Fab, Menu, Stack, Tab, Tabs, Typography } from '@mui/material';

// MUI Icons
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Custom Components
import MapCard from '@/ui/MapCard/MapCard';
import MapCardHeader from '@/ui/MapCard/MapCardHeader';
import MapCardAvatar from '@/ui/MapCard/MapCardAvatar';
import MapCardHeaderButtonClose from '@/ui/MapCard/MapCardHeaderButtonClose';
import MapCardHeaderButtonLayers from '@/ui/MapCard/MapCardHeaderButtonLayers';
import MapCardHeaderID from '@/ui/MapCard/MapCardHeaderID';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import GoogleMapsListItem from './LayerFunctionalities/GoogleMapsListItem';
import ShowOnGoogleMapsListItem from './LayerFunctionalities/ShowOnGoogleMapsListItem';
import StreetViewListItem from './LayerFunctionalities/StreetViewListItem';
import DialogContext from '@/context/DialogContext/DialogContext';
import GSInfoDialog from '@/components/Map/InfoCards/Dialogs/GSInfoDialog';
import useApi from '@/lib/api/useApi';

// Models
import modelData from '@/models/data';

// Types
import { IGFICardOverlayData, IGFIOverlayFeatureIndex } from '@/@types/views/map';
import { DCRecord } from '@/@types/lib/dataController';
import { IFieldsCollection } from '@/@types/models/model';
import EnumLayerFunctionality from '@/@types/services/gsServiceEnums';
import PosjedniciButton from './LayerFunctionalities/PosjedniciButton';
import VlasniciButton from './LayerFunctionalities/VlasniciButton';
import MapCardActions from '@/ui/MapCard/MapCardActions';
import Permissions from '@/lib/permissions';
import UserContext, { UserContextType } from '@/context/UserContext/UserContext';
import AttachDocumentButtonListItem from './LayerFunctionalities/AttachDocumentButtonListItem';
import KuceZaOdmorButton from './LayerFunctionalities/KuceZaOdmorButton';
import DetachListItem from './LayerFunctionalities/DetachListItem';
import { GFIFeatureType, IGIDStats } from '@/@types/services/gsService';
import ShowKOButton from './LayerFunctionalities/ShowKOButton';
import ShowPhotodocButton from './LayerFunctionalities/ShowPhotodocButton';
import ShowPhotodocButtonListItem from './LayerFunctionalities/ShowPhotodocButtonListItem';
import GMUkopaniButton from './LayerFunctionalities/GMUkopaniButton';
import AktivnostiOdrzavanjaButton from './LayerFunctionalities/AktivnostiOdrzavanjaButton';
import CommentListItem from './LayerFunctionalities/CommentListItem';
import Show3DButton from './LayerFunctionalities/Show3DButton';
import DocumentViews from '@/lib/documentViews';
import ClassifyButtonGroup from './LayerFunctionalities/ClassifyButtonGroup';
import Show3DButtonListItem from './LayerFunctionalities/Show3DButtonListItem';
import FICLocationCompositionButton from './LayerFunctionalities/FICLocationCompositionButton';
import DockListItem from './LayerFunctionalities/DockListItem';
import PPZahtjeviButton from './LayerFunctionalities/PPZahtjeviButton';
import OcijeniButton from './LayerFunctionalities/OcijeniButton';
import TerPotrosakDokumentiButton from './LayerFunctionalities/TerPotrosacDokumentiButton';
import GMKorisniciButton from './LayerFunctionalities/GMKorisniciButton';
import CommentView from '@/lib/commentViews';
import getGFIFormConfig from '@/lib/gfiLayout';

export interface GSInfoCardProps {
  featureData: IGFICardOverlayData;
  onClose: () => void;
  setIndex: React.Dispatch<React.SetStateAction<IGFIOverlayFeatureIndex>>;
  // why using any?????
  currentIndex: any;
  handlePopoverOverlayClick: any;
  layerCount: number;
  handleSetDetach: (newState: boolean) => void;
  isDetached: boolean;
  onDockRight?: () => void;
}

const GSInfoCard = (props: GSInfoCardProps) => {
  const { t } = useTranslation();

  const { featureData, onClose, setIndex, currentIndex, handlePopoverOverlayClick, layerCount, handleSetDetach, isDetached, onDockRight } =
    props;

  const [dataCache, setDataCache] = useState<Map<number, Map<String, DCRecord[] | DCRecord>>>(
    new Map<number, Map<String, DCRecord[] | DCRecord>>()
  ); // To save requests made by Menu List Items (e.g. Vlasnici, Posjednici fetch)

  const [entityStats, setEntityStats] = useState<IGIDStats>({ loading: false, documents: 0, comments: 0 });

  const { hideDialog, showDialog } = useContext(DialogContext);
  const userContext = useContext(UserContext) as UserContextType;

  const { hasAnyPermission } = userContext;

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const actionsMenuOpen = Boolean(actionsMenuAnchorEl);
  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchorEl(event.currentTarget);
  };
  const handleCloseActionsMenu = () => {
    setActionsMenuAnchorEl(null);
  };

  const { layerData } = featureData || {};

  const { layer, model, layerFunctionalityId, title } = layerData || {};

  const layerPickerDisabled = layerCount <= 1;

  const api = useApi();

  const modelFields = model?.fields && model?.fields.length > 0 ? model.fields : modelData.fields; // default to data.ts model

  const record = useMemo(() => {
    if (featureData) {
      if (featureData.record) {
        return featureData.record;
      }
      if (featureData.features[currentIndex[layer]]) {
        return featureData.features[currentIndex[layer]].properties;
      }
      if (featureData.features[0]) {
        return featureData.features[0].properties;
      }
    }
    return {};
  }, [featureData, currentIndex, layer]);

  useEffect(() => {
    if (featureData && featureData.features[0] && !featureData.record) {
      if (!currentIndex[layer]) {
        // if it's not already set initialize it
        setIndex((prev) => {
          const obj = { ...prev };
          obj[layer] = 0; // Move the state update to `useEffect`
          return obj;
        });
      }
    }
  }, [featureData, setIndex, layer]);

  const handleAddToCache = (identifier: string, recordId: number, data: DCRecord[] | DCRecord) => {
    setDataCache((prevCache) => {
      const newCache = new Map(prevCache); // Create a new Map from the previous state
      const recordCache = newCache.get(recordId) || new Map<string, DCRecord[]>();
      recordCache.set(identifier, data);
      newCache.set(recordId, recordCache);
      return newCache; // Return the new Map to trigger a state update
    });
  };

  const handleGetFromCache = (identifier: string, recordId: number): DCRecord[] | DCRecord | null => {
    const recordCache = dataCache.get(recordId);
    if (recordCache) {
      return recordCache.get(identifier) || null;
    }
    return null;
  };

  const getEntityStats = () => {
    if (record.gid && userContext.hasAnyPermission([Permissions.Private])) {
      setEntityStats((prev) => ({ ...prev, loading: true }));
      api
        .get(`core/entity-stats/${record.gid}`)
        .then((resp) => {
          if (resp.success) {
            setEntityStats({ ...(resp.data as IGIDStats), loading: false });
          } else {
            setEntityStats({ comments: 0, documents: 0, loading: false });
          }
        })
        .catch(() => {
          setEntityStats({ comments: 0, documents: 0, loading: false });
        });
    } else if (record.gid && userContext.hasAnyPermission([Permissions.PublicDocuments])) {
      setEntityStats((prev) => ({ ...prev, loading: true }));
      api
        .get(`core/entity-stats-public/${record.gid}`)
        .then((resp) => {
          if (resp.success) {
            setEntityStats({ ...(resp.data as IGIDStats), loading: false });
          } else {
            setEntityStats({ comments: 0, documents: 0, loading: false });
          }
        })
        .catch(() => {
          setEntityStats({ comments: 0, documents: 0, loading: false });
        });
    } else {
      setEntityStats({ comments: 0, documents: 0, loading: false });
    }
  };

  useEffect(() => {
    getEntityStats();
  }, [record.gid]);

  const generateCustomActions = (): ReactNode[] => {
    const nodes: ReactNode[] = [];

    switch (layerFunctionalityId) {
      case EnumLayerFunctionality.dkp_show_posjednici_vlasnici:
        nodes.push(
          <PosjedniciButton
            key="posjednici-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        nodes.push(
          <VlasniciButton
            key="vlasnici-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        break;
      case EnumLayerFunctionality.dkp_show_posjednici:
        nodes.push(
          <PosjedniciButton
            key="posjednici-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        break;
      case EnumLayerFunctionality.kuce_za_odmor_button:
        nodes.push(
          <KuceZaOdmorButton
            key="kzo-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
            handleCloseMenu={() => {
              hideDialog();
            }}
          />
        );
        break;
      case EnumLayerFunctionality.objekti_ko_photodoc:
        nodes.push(<ShowKOButton key="ko-btn" featureData={featureData} layerIndex={currentIndex[featureData.layerData.layer]} />);
        nodes.push(
          <ShowPhotodocButton key="photodoc-btn" featureData={featureData} layerIndex={currentIndex[featureData.layerData.layer]} />
        );
        break;
      case EnumLayerFunctionality.grobna_mjesta_show_ukopani:
        nodes.push(
          <GMKorisniciButton
            key="korisnici-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        nodes.push(
          <GMUkopaniButton
            key="ukopani-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        break;
      case EnumLayerFunctionality.classify:
        nodes.push(<ClassifyButtonGroup key="classify-btns" record={record} closeInfoCard={onClose} />);
        break;
      case EnumLayerFunctionality.fic_lokacije_show_data:
        nodes.push(
          <FICLocationCompositionButton
            key="composition-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        break;
      case EnumLayerFunctionality.paths_show_activities:
        nodes.push(
          <AktivnostiOdrzavanjaButton
            key="odrzavanje-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        break;
      case EnumLayerFunctionality.pp_zahtjevi_button:
        nodes.push(<PPZahtjeviButton record={record} />);
        break;
      case EnumLayerFunctionality.prisupacnost_ocijeni_button:
        nodes.push(<OcijeniButton record={record} />);
        break;
      case EnumLayerFunctionality.ter_potrosac_dokumenti_button:
        nodes.push(
          <TerPotrosakDokumentiButton
            key="ter-dokumenti-btn"
            featureData={featureData}
            record={record}
            onToggle={handleOpenDialog}
            addToCache={handleAddToCache}
            getFromCache={handleGetFromCache}
          />
        );
        break;
      default:
        break;
    }
    if (hasAnyPermission([Permissions.ThreeDView]) && layerFunctionalityId !== EnumLayerFunctionality.classify && !hasAnyPermission([Permissions.ThreeDHideGFIButton])) {
      nodes.push(
        // Will not render if client does not have permission for 3D
        <Show3DButton key="show-3d-btn" featureData={featureData} />
      );
    }

    return nodes;
  };

  const generateActionList = (): ReactNode[] => {
    const nodes: ReactNode[] = [];

    if (hasAnyPermission([Permissions.ThreeDView]) && layerFunctionalityId === EnumLayerFunctionality.classify) {
      nodes.push(
        // Will not render if client does not have permission for 3D
        <Show3DButtonListItem key="show-3d-li" handleCloseMenu={handleCloseActionsMenu} featureData={featureData} />
      );
    }

    if (record.gid) {
      if (userContext?.hasAnyPermission([Permissions.Private])) {
        nodes.push(
          <AttachDocumentButtonListItem
            stats={{ loading: entityStats.loading, count: entityStats.documents }}
            key="attach-document-li"
            handleCloseMenu={handleCloseActionsMenu}
            gid={record.gid as string}
            onDialogClose={getEntityStats}
          />,
          <CommentListItem
            stats={{ loading: entityStats.loading, count: entityStats.comments }}
            key="comment-li"
            handleCloseMenu={handleCloseActionsMenu}
            gid={record.gid as string}
            feature={featureData.features[currentIndex[layer]]}
            onDialogClose={getEntityStats}
          />
        );
      } else {
        if (userContext?.hasAnyPermission([Permissions.PublicDocuments])) {
          nodes.push(
            <AttachDocumentButtonListItem
              stats={{ loading: entityStats.loading, count: entityStats.documents }}
              key="attach-document-li"
              handleCloseMenu={handleCloseActionsMenu}
              gid={record.gid as string}
              onDialogClose={getEntityStats}
              view={DocumentViews.View}
            />
          );
        }
        if (userContext?.hasAnyPermission([Permissions.PublicComments])) {
          nodes.push(
            <CommentListItem
              stats={{ loading: false, count: 0 }}
              key="comment-li"
              handleCloseMenu={handleCloseActionsMenu}
              gid={record.gid as string}
              feature={featureData.features[currentIndex[layer]]}
              onDialogClose={() => {}}
              view={CommentView.Add}
            />
          );
        }
      }
    }

    switch (layerFunctionalityId) {
      case EnumLayerFunctionality.odvoz_location_button:
        break;
      case EnumLayerFunctionality.kuce_za_odmor_button:
        break;
      case EnumLayerFunctionality.grobna_mjesta_show_ukopani:
        nodes.push(
          <ShowPhotodocButtonListItem key="photodoc-btn" featureData={featureData} type="grobno_mjesto"/>
        );
        break;
      case EnumLayerFunctionality.google_maps_navigate_button:
        nodes.push(<GoogleMapsListItem key="google-maps-li" featureData={featureData} handleCloseMenu={handleCloseActionsMenu} />);
        break;
      default:
        break;
    }

    nodes.push(<ShowOnGoogleMapsListItem key="show-on-google-maps-li" featureData={featureData} handleCloseMenu={handleCloseActionsMenu} />);
    nodes.push(<StreetViewListItem key="street-view-li" featureData={featureData} handleCloseMenu={handleCloseActionsMenu} />);

    nodes.push(
      <DetachListItem isDetached={isDetached} key="detach-li" handleDetach={handleSetDetach} closeMenu={handleCloseActionsMenu} />
    );

    if (onDockRight) {
      nodes.push(<DockListItem key="dock-right-li" handleDock={onDockRight} closeMenu={handleCloseActionsMenu} />);
    }
    return nodes;
  };

  const fields = modelFields;
  const { fieldNames, layout } = useMemo(
    () => getGFIFormConfig(modelFields, model?.sections, model?.layout),
    [model?.layout, model?.sections, modelFields]
  );

  const recordId = record.id ? (record.id as string | number) : record.fid ? (record.fid as string | number) : null;

  // const handleToggleKuceZaOdmor = () => {
  //   const newValue = !expandedKuceZaOdmor;
  //   setExpandedKuceZaOdmor(newValue);
  // };

  // const handleOdvozLocationRedirect = () => {
  //   const locId =
  //     initialRecod.lokacija_i === null
  //       ? ""
  //       : `&loc_id=${initialRecod.lokacija_i}`;
  //   if (locId !== "") {
  //     const url =
  //       location.origin +
  //       "/komunalne-obveze/?" +
  //       locId.toString() +
  //       "&open_first=true";
  //     window.open(url, "_blank");
  //   } else {
  //     const url = location.origin + "/komunalne-obveze";
  //     window.open(url, "_blank");
  //   }
  // };

  // const handleKomunaneObvezeRedirect = () => {
  //   const locId =
  //   initialRecod.lokacija_id === null
  //   ? ""
  //   : `&loc_id=${initialRecod.lokacija_id}`;
  //   if (locId !== "") {
  //     const url =
  //       location.origin +
  //       "/komunalne-obveze/?" +
  //       locId.toString() +
  //       "&open_first=true";
  //     window.open(url, "_blank");
  //   } else {
  //     const url = location.origin + "/komunalne-obveze";
  //     window.open(url, "_blank");
  //   }
  // };

  // const handleFotodokumentacijaRedirect = () => {
  //   const objektId =
  //     initialRecod.objekt_id === null
  //       ? ""
  //       : `${initialRecod.objekt_id}`;
  //   console.log(initialRecod)
  //   if (objektId !== "") {
  //     const url =
  //       location.origin +
  //       "/fotodokumentacija-objekata/?rec_id=" + objektId.toString();
  //     window.open(url, "_blank");
  //   } else {
  //     const url = location.origin + "/fotodokumentacija-objekata";
  //     window.open(url, "_blank");
  //   }
  // };

  const modelTitle = (
    model?.title_type === 1
      ? t(`${model.title_token}`)
      : model?.title_type === 2 && model?.title_field
      ? record[model.title_field]
      : model?.title_type === 3 && model?.title_field
      ? `${t(`${model.title_token}`)}: ${record[model.title_field]}`
      : null
  ) as string | null;

  const parsedTitle =
    model && modelTitle
      ? modelTitle
      : record.naziv
      ? (record.naziv as string)
      : title
      ? t(title)
      : layer
      ? t(`map:layer.${layer}`)
      : 'Info';

  const GSFormContent = useMemo(
    () => (
      <GridContainer width="100%" spacing={0}>
        <FormContent fields={fields as IFieldsCollection} fieldNames={fieldNames} layout={layout} columns={1} mode="simpleView" record={record} collapsibleSections />
      </GridContainer>
    ),
    [fields, fieldNames, layout, record]
  );

  const handleOpenDialog = (node: ReactNode, identifier: string, hideFormContent: boolean = false) => {
    showDialog(GSInfoDialog, {
      body: node,
      handleClose: () => {
        hideDialog();
      },
      identifier,
      FormContent: !hideFormContent ? GSFormContent : null,
    });
  };

  const customActionList = generateActionList();
  const customActions = generateCustomActions();

  return (
    <MapCard
      sx={{
        backgroundColor: 'background.default',
        width: 'fit-content',
        // the OL popup parent has no width, so fit-content collapses to min-content without this
        minWidth: '240px',
        maxWidth: { xs: '300px', md: '500px' },
      }}
    >
      <MapCardHeader
        title={
          <Typography
            color="white"
            fontSize="0.9rem"
            lineHeight={1.15}
            variant="body1"
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              // mobile header is narrow, so allow 2 lines there instead of cutting the name short
              display: { xs: '-webkit-box', md: 'block' },
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: { xs: 2 },
              // desktop: nowrap makes the title set the card width (fit-content)
              whiteSpace: { md: parsedTitle && parsedTitle.length > 45 ? 'normal' : 'nowrap' },
              textOverflow: 'ellipsis',
            }}
          >
            {parsedTitle}
          </Typography>
        }
        subheader={
          recordId && recordId !== null ? (
            <Stack direction="row" alignItems="center" color="white" marginLeft="-4px" spacing={0.5}>
              <MapCardHeaderID id={recordId} />
              <Typography
                noWrap
                fontSize="0.85rem"
                lineHeight={1.15}
                sx={{ maxWidth: '100%' }}
                variant="body2"
              >
                {recordId}
              </Typography>
            </Stack>
          ) : null
        }
        disableTypography
        avatar={<MapCardAvatar ariaLabel="gfi_icon" icon={model?.icon ?? 'info'} />}
        action={
          <Stack m={0} direction="row">
            {!layerPickerDisabled && <MapCardHeaderButtonLayers layerCount={layerCount} onClick={handlePopoverOverlayClick} />}
            <MapCardHeaderButtonClose onClick={onClose} />
          </Stack>
        }
      />
      {featureData.features && featureData.features.length > 1 ? (
        <Box width="100%" borderBottom="0.1px solid lightgray">
          <Tabs
            sx={{ minHeight: '24px', height: '24px' }}
            variant="scrollable"
            scrollButtons="auto"
            value={currentIndex[layer]}
            onChange={(event, value) => {
              setIndex((prev: any) => {
                const obj = { ...prev };
                obj[layer] = value;
                return obj;
              });
            }}
            aria-label="pagination tabs"
            TabScrollButtonProps={{
              sx: {
                transition: 'width 0.15s',
                '&.Mui-disabled': {
                  width: 0,
                },
              },
            }}
          >
            {featureData.features.map((feat: GFIFeatureType, index: number) => (
              <Tab
                key={`feature-tab-${feat.id}}`}
                sx={{
                  flexGrow: 1,
                  borderRight: index !== featureData.features.length - 1 ? '0.1px solid lightgray' : '',
                  p: 0,
                  height: '24px',
                  minHeight: '24px',
                  minWidth: '48px',
                }}
                label={index + 1}
              />
            ))}
          </Tabs>
        </Box>
      ) : null}
      <CardContent
        sx={{
          p: 1,
          '&:last-child': {
            paddingBottom: 1,
          },
          width: '100%',
        }}
      >
        <Box display="flex" gap={1} width="100%">
          <GridContainer spacing={0} width="100%">
            {GSFormContent}
          </GridContainer>
          {!customActions.length && customActionList.length ? (
            <>
              <Fab
                color="primary"
                aria-label="more"
                id="long-button"
                aria-controls={actionsMenuOpen ? 'long-menu' : undefined}
                aria-expanded={actionsMenuOpen ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleOpenActionsMenu}
                size="small"
                sx={{
                  minWidth: '32px',
                  minHeight: '32px',
                  height: '32px',
                  width: '32px',
                  alignSelf: 'flex-end',
                }}
              >
                <MoreVertIcon fontSize="medium" />
              </Fab>
              <Menu
                id="long-menu"
                MenuListProps={{
                  'aria-labelledby': 'long-button',
                }}
                sx={{ p: 0 }}
                anchorEl={actionsMenuAnchorEl}
                open={actionsMenuOpen}
                onClose={handleCloseActionsMenu}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                {customActionList}
              </Menu>
            </>
          ) : null}
        </Box>
      </CardContent>
      {customActions.length ? (
        <MapCardActions>
          <Box display="flex" justifyContent="space-between" width="100%" sx={{ gap: { xs: 1, md: 0 } }}>
            <Box display="flex" width="100%" sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
              {customActions}
            </Box>
            {customActionList.length ? (
              <Box width="fit-content" display="flex" alignItems="flex-end">
                <Fab
                  color="primary"
                  aria-label="more"
                  id="long-button"
                  aria-controls={actionsMenuOpen ? 'long-menu' : undefined}
                  aria-expanded={actionsMenuOpen ? 'true' : undefined}
                  aria-haspopup="true"
                  onClick={handleOpenActionsMenu}
                  size="small"
                  sx={{
                    minWidth: '32px',
                    minHeight: '32px',
                    height: '32px',
                    width: '32px',
                    alignSelf: 'flex-end',
                  }}
                >
                  <MoreVertIcon fontSize="medium" />
                </Fab>
                <Menu
                  id="long-menu"
                  MenuListProps={{
                    'aria-labelledby': 'long-button',
                  }}
                  sx={{ p: 0 }}
                  anchorEl={actionsMenuAnchorEl}
                  open={actionsMenuOpen}
                  onClose={handleCloseActionsMenu}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                  {customActionList}
                </Menu>
              </Box>
            ) : null}
          </Box>
        </MapCardActions>
      ) : null}
    </MapCard>
  );
};

export default GSInfoCard;
